import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePUR6010_R01Store } from "./Store_PUR6010_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPUR6010_R01HeaderDto, PUR6010_R01HeaderDto } from "./Dto_PUR6010_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR6010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PUR6010_R01HeaderDto> | null>;
	defaultPUR6010_R01HeaderRow: PUR6010_R01HeaderDto;
	handleCreateClick: () => void;
}

export const Table_PUR6010_R01 = (props: Props) => {

	const { gridRef, defaultPUR6010_R01HeaderRow, handleCreateClick } = props;

	const { PUR6010_R01List, 
		selectedPUR6010_R01, setSelectedPUR6010_R01, initSelectedPUR6010_R01,
		columnState, setColumnState, 
		editPUR6010_R01,
		PUR6010_R01DetailList, setPUR6010_R01DetailList,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = usePUR6010_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'tradeNo',
		gridData: PUR6010_R01List,
		defaultGridData: defaultPUR6010_R01HeaderRow,
		selectedRow: selectedPUR6010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		tradeDt: '지불일자',
		customNm: '거래처명',
		tradeAmt: '지불합계',
		cashAmt: '현금',
		bankbookAmt: '계좌이체',
		noteAmt: '어음',
		cmsAmt: 'CMS',
		dcAmt: '할인',
		advancesFee: '선급금',
		remark: '비고',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<PUR6010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '지불일자', colId: 'tradeDt', field: 'tradeDt', width: 119,
			cellStyle: (p) => getCellStyle(p, 'tradeDt'),
			valueFormatter: (p: { data?: PUR6010_R01HeaderDto }) =>
				p.data?.tradeDt ? formatDate(new Date(p.data.tradeDt)) : '',
			comparator: cellDateComparator,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '지불합계', colId: 'tradeAmt', field: 'tradeAmt', width: 125,
			cellStyle: (p) => getCellStyle(p, 'tradeAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
		},
		{
			headerName: '현금', colId: 'cashAmt', field: 'cashAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'cashAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '계좌이체', colId: 'bankbookAmt', field: 'bankbookAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'bankbookAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '어음', colId: 'noteAmt', field: 'noteAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'noteAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
			headerClass: aggridHeaderClass,
		},
		{
			headerName: 'CMS', colId: 'cmsAmt', field: 'cmsAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'cmsAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '할인', colId: 'dcAmt', field: 'dcAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'dcAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '선급금', colId: 'advancesFee', field: 'advancesFee', width: 100,
			cellStyle: (p) => getCellStyle(p, 'advancesFee'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 150,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			headerClass: aggridHeaderClass,
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PUR6010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PUR6010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs]);


	// 마운트 시 컬럼 상태 복원
	
	const handleRowClick = useCallback((e: RowClickedEvent<PUR6010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 전표정보와 같은지 확인
		const isSelected = selectedPUR6010_R01?.tradeNo === rowData.tradeNo;
		if (isSelected) {
			setSelectedPUR6010_R01(undefined);
			setDetailScrollTop(null);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedPUR6010_R01(rowData);
		}
	}, [selectedPUR6010_R01, setSelectedPUR6010_R01, setDetailScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		tradeDt: PUR6010_R01List.length.toLocaleString() + "건",
		tradeAmt: PUR6010_R01List.reduce((sum, item) => (sum + item.tradeAmt), 0).toLocaleString(),
		cashAmt: PUR6010_R01List.reduce((sum, item) => (sum + (item.cashAmt ?? 0)), 0).toLocaleString(),
		bankbookAmt: PUR6010_R01List.reduce((sum, item) => (sum + (item.bankbookAmt ?? 0)), 0).toLocaleString(),
		noteAmt: PUR6010_R01List.reduce((sum, item) => (sum + (item.noteAmt ?? 0)), 0).toLocaleString(),
		cmsAmt: PUR6010_R01List.reduce((sum, item) => (sum + (item.cmsAmt ?? 0)), 0).toLocaleString(),
		dcAmt: PUR6010_R01List.reduce((sum, item) => (sum + (item.dcAmt ?? 0)), 0).toLocaleString(),
		advancesFee: PUR6010_R01List.reduce((sum, item) => (sum + (item.advancesFee ?? 0)), 0).toLocaleString(),
		editable: false,
	}]), [PUR6010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PUR6010_R01List, selectedPUR6010_R01]);

	return (
		
		<GridContextWrapper<PUR6010_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedPUR6010_R01}
			defaultRowData={defaultPUR6010_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<PUR6010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<PUR6010_R01HeaderDto>({
					idKey: 'tradeNo',
					selectedList: selectedPUR6010_R01 ? [selectedPUR6010_R01.tradeNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
